import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { useAppStatus } from "@/lib/storeLinks";
import { Reveal } from "@/components/motion";

export const StatusChip = ({ status, light, testid }) => {
  const { t } = useLang();
  const glow = status === "pending" || status === "live";
  const label = status === "live" ? t.status.live : status === "pending" ? t.status.pending : t.status.dev;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${light ? "border-black/10 bg-white/60 text-black/70" : "border-white/15 bg-white/5 text-white/75"}`}>
      <span className={`relative h-1.5 w-1.5 rounded-full ${glow ? "bg-[#5cf2a0]" : "bg-amber-400"}`}>
        {glow && <span className="absolute inset-0 animate-ping rounded-full bg-[#5cf2a0]" />}
      </span>
      {label}
    </span>
  );
};

const LogoFrame = ({ app, className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="absolute -inset-[18%] rounded-full opacity-60 blur-3xl transition-opacity duration-700 group-hover:opacity-100" style={{ background: `radial-gradient(circle, ${app.accent}55, transparent 65%)` }} />
    <div className="relative aspect-square overflow-hidden rounded-[26%] shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)] ring-1 ring-white/15">
      <img
        src={app.logo}
        alt={`${app.name} logo`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:rotate-[-3deg]"
        style={{ "--s": app.logoScale, transform: `scale(var(--s))` }}
      />
    </div>
  </div>
);

export const AppCard = ({ app, index, variant, className = "" }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const status = useAppStatus(app);
  const light = app.theme === "light";
  const muted = light ? "text-[#1b1440]/60" : "text-white/55";
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const hero = variant === "hero";
  const wide = variant === "wide";

  return (
    <Reveal delay={index * 0.08} className={className}>
      <Link
        ref={ref}
        to={`/apps/${app.slug}`}
        onMouseMove={onMove}
        data-testid={`app-card-${app.slug}`}
        className={`app-card group relative flex h-full flex-col overflow-hidden rounded-[32px] border p-6 sm:p-9 ${light ? "border-black/5 text-[#140f2e]" : "border-white/10 text-white"}`}
        style={{ background: app.cardBg, "--accent": app.accent }}
      >
        <div className="spot pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
          <span className={`font-mono text-[11px] uppercase tracking-[0.25em] ${muted}`}>
            {String(index + 1).padStart(2, "0")} / {app.category[lang]}
          </span>
          <StatusChip status={status} light={light} testid={`app-card-status-${app.slug}`} />
        </div>

        <div className={`relative z-10 flex flex-1 gap-8 pt-8 ${wide ? "flex-col-reverse md:flex-row md:items-center md:justify-between" : hero ? "flex-col" : "flex-row items-end justify-between"}`}>
          {hero && <LogoFrame app={app} className="mx-auto my-auto w-[58%] max-w-[360px]" />}
          <div className={wide ? "max-w-xl" : ""}>
            <h3 className={`font-display font-bold leading-[0.9] tracking-[-0.04em] ${hero || wide ? "text-5xl sm:text-7xl" : "text-4xl sm:text-5xl"}`}>{app.name}</h3>
            <p className="mt-3 text-lg font-medium sm:text-xl">{app.tagline[lang]}</p>
            {(hero || wide) && <p className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base ${muted}`}>{app.short[lang]}</p>}
            <span className="mt-6 inline-flex items-center gap-3 text-sm font-semibold">
              <span className={`grid h-11 w-11 place-items-center rounded-full border transition-[transform,background-color,color,border-color] duration-500 ease-[cubic-bezier(.16,1,.3,1)] group-hover:rotate-45 group-hover:border-transparent group-hover:bg-[var(--accent)] group-hover:text-[#05070f] ${light ? "border-black/15" : "border-white/20"}`}>
                <ArrowUpRight className="h-4 w-4" />
              </span>
              {t.apps.view}
            </span>
          </div>
          {!hero && <LogoFrame app={app} className={wide ? "w-40 shrink-0 sm:w-56" : "w-28 shrink-0 sm:w-36"} />}
        </div>
      </Link>
    </Reveal>
  );
};
